import { Injectable } from '@angular/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { LocalStorage } from 'ngx-store'
import { Logger } from 'common/log'
import { HttpClient } from '@angular/common/http'
import { firstValueFrom, timeout } from 'rxjs'
import { ElementsClientNotRunningModalComponent } from '../components/elementsClientNotRunningModal.component'

class ClientAuthRejected extends Error {
  constructor () {
    super('ELEMENTS client rejected authorization')
  }
}

@Injectable({ providedIn: 'root' })
export class ElementsClientService {
  logger = new Logger(__filename)
  connected = false
  port: null|number
  private initialPort = 31335
  private maxPorts = 4

  @LocalStorage('clientAccessKey') storedClientAccessKey = ''

  constructor (
    private http: HttpClient,
    private ngbModal: NgbModal,
  ) { }

  async detect (): Promise<number|null> {
    this.logger.debug('Detecting')
    for (let port = this.initialPort; port < this.initialPort + this.maxPorts; port++) {
      try {
        const response = await this.testConnection(port)
        this.logger.info(`Detected ELEMENTS client ${response.version} on port ${port}`)
        this.connected = true
        this.port = port
        return port
      } catch (_error) {
      }
    }
    this.port = null
    this.logger.info('No ELEMENTS client found')
    return null
  }

  async testConnection (port: number|null): Promise<any> {
    const query$ = this.http.jsonp(`http://localhost:${port}/app/info`, 'callback')
    return firstValueFrom(query$.pipe(timeout(1000)))
  }

  async authorize (): Promise<void> {
    const response: any = await firstValueFrom(this.http
      .jsonp(
        `http://localhost:${this.port}/access/request?key=${this.storedClientAccessKey}&name=ELEMENTS`,
        'callback',
      ))
    if (response.state !== 'allowed') {
      throw new ClientAuthRejected()
    }
    this.storedClientAccessKey = response.key
  }

  async ensureConnected (): Promise<void> {
    while (true) {
      await this.detect()

      if (this.port) {
        break
      }

      try {
        await this.ngbModal.open(ElementsClientNotRunningModalComponent).result
      } catch {
        break
      }
    }
  }

  async ensureAuthorized (): Promise<void> {
    while (true) {
      try {
        await this.authorize()
        break
      } catch (error) {
        if (error instanceof ClientAuthRejected) {
          continue
        }
        throw error
      }
    }
  }

  async refresh (): Promise<void> {
    if (this.connected) {
      try {
        await this.testConnection(this.port)
      } catch (_err) {
        this.connected = false
        await this.detect()
      }
    } else {
      await this.detect()
    }
  }

  async mount (workspaceId: number): Promise<void> {
    await this.ensureConnected()
    await this.ensureAuthorized()
    await firstValueFrom(this.http.jsonp(
      `http://localhost:${this.port}/app/mount/${workspaceId}?key=${this.storedClientAccessKey}`,
      'callback',
    ))
  }

  activateURI (uri: string): void {
    this.logger.info('activating URI', uri)
    location.href = uri
  }

  generateURI (command: string, params: Record<string, any>): string {
    const query = Object.keys(params).map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k])).join('&')
    return `elements-client://${command}?${query}`
  }

  showFile (path: string, workspaceId: number|null = null): void {
    this.activateURI(this.generateURI('show', { path, workspace_id: workspaceId }))
  }

  browseToFile (path: string, workspaceId: number): void {
    this.activateURI(this.generateURI('browse', { path, workspace_id: workspaceId }))
  }

  tryStart (): void {
    this.activateURI(this.generateURI('start', {}))
  }
}
